import { useState, useCallback, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { RouteErrorBoundary } from "@/components/shared/ErrorBoundary";
import { cn } from "@/lib/utils";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { MobileNav } from "./MobileNav";
import { CommandPalette } from "@/components/ui/CommandPalette";
import { EmailVerificationBanner } from "@/components/shared/EmailVerificationBanner";
import { PendingDeletionBanner } from "@/components/shared/PendingDeletionBanner";
import { InstallPromptBanner } from "@/components/shared/InstallPromptBanner";
import { UpdateAvailableBanner } from "@/components/shared/UpdateAvailableBanner";
import { TrialBanner } from "@/components/shared/TrialBanner";
import {
  TrialExpiredLockout,
  pathBypassesTrialLockout,
} from "@/components/shared/TrialExpiredLockout";
import { DemoBanner } from "@/components/shared/DemoBanner";
import { DemoTour } from "@/components/shared/DemoTour";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { useAuth } from "@/hooks/useAuth";
import { useDemoSession } from "@/hooks/useDemoSession";
import { usePlanFeatures } from "@/hooks/usePlanFeatures";
import { useStaleTabReload } from "@/hooks/useStaleTabReload";
import { getOnboardingState } from "@/api/signup";

export function AppShell() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isDemo } = useDemoSession();
  const { features } = usePlanFeatures();

  // Trial-end lockout. Once ``is_trial_expired`` is true we render a
  // full-screen "Choose a plan" view in place of the outlet — UNLESS
  // the user is already inside settings/billing (where they need to
  // actually pick a plan). Demo sessions and internal/founder orgs
  // bypass the check entirely so we never lock out our own tenants
  // or the marketing-site demo. The route module-gate on the
  // backend independently 403s any non-allowed module for
  // trial_expired tenants — this is purely the UX layer.
  const trialLockedOut =
    !isDemo &&
    features?.is_trial_expired === true &&
    features?.is_internal !== true &&
    !pathBypassesTrialLockout(location.pathname);

  // Reload the page when returning to a tab that's been hidden for 30+
  // minutes. Long-idle SPA tabs eventually OOM the Chrome renderer
  // ("Aw, Snap! Error code: 5") regardless of cleanup discipline; a fresh
  // load resets the heap before the user notices anything is off.
  useStaleTabReload();

  // Auto-redirect admins to the onboarding wizard on first login. The
  // wizard itself renders outside the shell, so the only trigger is when
  // an admin lands on any shell route with onboarding not yet completed.
  // Non-admin roles (EMPLOYEE/MANAGER) are never bounced — they can't
  // complete the wizard anyway.
  useEffect(() => {
    if (!user) return;
    if (user.role !== "SUPER_ADMIN" && user.role !== "HR_ADMIN") return;
    // Demo sessions should never be bounced to /onboarding — the demo
    // write-guard blocks the wizard's POSTs so the user would be stuck in
    // a dead-end loop.
    if (isDemo) return;
    // Skip the check if we've already decided to land on /onboarding —
    // the onboarding page itself handles its own state.
    if (location.pathname.startsWith("/onboarding")) return;
    let cancelled = false;
    getOnboardingState()
      .then((s) => {
        if (cancelled) return;
        if (!s.completed) navigate("/onboarding", { replace: true });
      })
      .catch(() => {
        /* non-fatal: don't block the app if the state endpoint hiccups. */
      });
    return () => {
      cancelled = true;
    };
    // Intentionally only re-runs on user change — we don't want to
    // re-check on every navigation inside the shell.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Collapse sidebar on mobile
  useEffect(() => {
    if (isMobile) setSidebarCollapsed(true);
  }, [isMobile]);

  // Cmd+K handler
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const toggleSidebar = useCallback(
    () => setSidebarCollapsed((c) => !c),
    []
  );

  return (
    /* min-h-screen here is intentional — body owns the bento gradient
       (see globals.css). We let it shine through; do NOT set a flat
       bg-background here or the gradient will be hidden. */
    <div className="min-h-screen">
      <DemoBanner />
      <DemoTour />
      {/*
        Skip link — visible only when focused by keyboard. Styled via the
        .skip-link helper in globals.css so it reliably clears whatever
        z-indexed chrome the sidebar uses.
      */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
        isMobile={isMobile}
      />

      {/* Backdrop for the mobile drawer — only renders while the
          drawer is open on a phone-sized viewport. Tapping it closes
          the drawer. */}
      {isMobile && !sidebarCollapsed && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={toggleSidebar}
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
        />
      )}

      <div
        className={cn(
          "transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
          // Sidebar widths (76px collapsed / 264px expanded) come from
          // the new Sidebar component. On mobile the sidebar is an
          // overlay, so content takes the full width. Bottom padding
          // leaves room for MobileNav.
          isMobile
            ? "ml-0 pb-16"
            : sidebarCollapsed
              ? "ml-[76px]"
              : "ml-[264px]"
        )}
      >
        <TopBar
          onSearchOpen={() => setSearchOpen(true)}
          onMenuOpen={isMobile ? toggleSidebar : undefined}
        />

        <main
          id="main-content"
          tabIndex={-1}
          className="p-4 md:p-6 focus:outline-none"
        >
          <PendingDeletionBanner />
          <EmailVerificationBanner />
          <UpdateAvailableBanner />
          <InstallPromptBanner />
          <TrialBanner />
          {/* Per-route error boundary — a render crash on, say, the
              reports page must not break the sidebar/top bar or the
              dashboard the user wants to fall back to. The boundary
              keys on the pathname, so navigating resets it. */}
          <RouteErrorBoundary>
            {trialLockedOut ? (
              <TrialExpiredLockout plan={features?.plan ?? null} />
            ) : (
              <Outlet />
            )}
          </RouteErrorBoundary>
        </main>
      </div>

      {isMobile && <MobileNav />}

      <CommandPalette isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
